import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MolecularBackdrop } from '../components/MolecularBackdrop';
import { Sparkles, Ticket, Clock, MapPin } from 'lucide-react';
interface LeadData {
  name: string;
  email: string;
  company: string;
  role: string;
}
interface Props {
  onSubmit: (data: LeadData) => void;
  onSkip: () => void;
  onOpenMap: () => void;
}
export function SlideLead({
  onSubmit,
  onSkip,
  onOpenMap
}: Props) {
  const [formData, setFormData] = useState<LeadData>({
    name: '',
    email: '',
    company: '',
    role: ''
  });
  const isValid = !!formData.name && !!formData.company && !!formData.role && formData.email.includes('@');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) onSubmit(formData);
  };
  const inputClass = 'w-full h-12 px-4 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-minerva-blue focus:ring-2 focus:ring-minerva-blue/20 transition-all text-sm';
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} transition={{
    duration: 0.4,
    ease: 'easeOut'
  }} className="flex-1 flex flex-col relative px-5 pt-16 pb-8 overflow-y-auto" data-id="element-593">
      <MolecularBackdrop variant="bloom" data-id="element-594" />

      <div className="relative z-10 flex-1 flex flex-col" data-id="element-595">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="mb-5 text-center mt-2" data-id="element-596">
          <div className="w-12 h-12 mx-auto bg-minerva-blue/10 rounded-full flex items-center justify-center mb-3" data-id="element-597">
            <Ticket size={24} className="text-minerva-blue" data-id="element-598" />
          </div>
          <h2 className="text-[28px] font-display font-bold text-slate-900 leading-tight mb-2" data-id="element-599">
            You're in the draw
          </h2>
          <p className="text-slate-600 text-[13px] leading-relaxed max-w-[280px] mx-auto" data-id="element-600">
            Thanks for taking part in today’s SNMMI Industry Pulse. Enter your
            details below to activate your raffle entry.
          </p>
        </motion.div>

        <motion.form initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} onSubmit={handleSubmit} className="flex flex-col gap-3" data-id="element-601">
          <input type="text" placeholder="Full name" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} className={inputClass} required data-id="element-602" />
          <input type="email" placeholder="Email address" value={formData.email} onChange={e => setFormData({
          ...formData,
          email: e.target.value
        })} className={inputClass} required data-id="element-603" />
          <input type="text" placeholder="Company" value={formData.company} onChange={e => setFormData({
          ...formData,
          company: e.target.value
        })} className={inputClass} required data-id="element-604" />
          <input type="text" placeholder="Role / Title" value={formData.role} onChange={e => setFormData({
          ...formData,
          role: e.target.value
        })} className={inputClass} required data-id="element-605" />

          {/* Booth Incentive Block */}
          <button type="button" onClick={onOpenMap} className="mt-2 relative rounded-xl p-[1px] bg-gradient-to-r from-minerva-blue/40 to-minerva-cyan/40 w-full text-left hover:opacity-90 transition-opacity" data-id="element-606">
            <div className="bg-white rounded-[11px] p-3 flex items-center gap-3" data-id="element-607">
              <div className="w-8 h-8 rounded-full bg-minerva-blue/10 flex items-center justify-center shrink-0" data-id="element-608">
                <Sparkles size={16} className="text-minerva-blue" data-id="element-609" />
              </div>
              <div className="flex flex-col" data-id="element-610">
                <span className="text-[11px] font-bold text-slate-900" data-id="element-611">
                  Want to improve your odds?
                </span>
                <span className="text-[11px] text-slate-600 flex items-center gap-1" data-id="element-612">
                  Visit <MapPin size={10} className="text-minerva-blue" data-id="element-613" />{' '}
                  <strong className="text-minerva-blue underline underline-offset-2" data-id="element-614">
                    Booth #1762
                  </strong>{' '}
                  to DOUBLE your chances.
                </span>
              </div>
            </div>
          </button>

          <div className="mt-4 flex flex-col gap-3" data-id="element-615">
            <motion.button whileTap={{
            scale: isValid ? 0.97 : 1
          }} type="submit" disabled={!isValid} className={`w-full h-14 rounded-2xl font-semibold text-base transition-all duration-300 flex flex-col items-center justify-center ${isValid ? 'bg-minerva-blue text-white shadow-[0_8px_24px_-6px_rgba(0,133,189,0.5)] hover:bg-minerva-dark' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`} data-id="element-616">
              <span data-id="element-617">Enter today's draw</span>
            </motion.button>

            <div className="flex items-center justify-center gap-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest" data-id="element-618">
              <Clock size={12} data-id="element-619" />
              <span data-id="element-620">Entries close daily at 4:00 PM</span>
            </div>
          </div>
        </motion.form>
      </div>
    </motion.div>;
}